import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { InstallView, ListingDetail } from '@dunea/shared';
import { apiGetListing, sendListingEvent, type InstallSource } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { installListing, remixTemplate, requestApplyTheme } from './install-actions';
import {
  apiErrorMessage,
  coerceThemeTokens,
  consentSentence,
  creditsLabel,
  defaultSpendCap,
  needsSpendCap,
  parseSpendCap,
  remixLabel,
  safeHttpsUrl,
  SPEND_CAP_SENTENCE,
  initialSettingsValues,
  installReadiness,
  installStatusMeta,
  installsLabel,
  listingSettingsFields,
  priceLabel,
  ratingSummary,
  requestedPermissions,
  settingsErrors,
  settingsPayload,
} from './marketplace-model';
import { DeveloperLine, ListingBadges, ListingIcon, Stars, ThemePreview } from './parts';
import { ListingReviews } from './ListingReviews';
import { SettingsForm } from './SettingsForm';

/** Listing detail, drawn over the Marketplace panel: what it does, consent, settings, install, reviews. */
export function ListingDrawer({
  appId,
  slug,
  install,
  source = 'workbench',
  onClose,
  onInstalled,
  onManage,
}: {
  appId: string;
  slug: string;
  install: InstallView | null;
  /** Where the owner came from (storefront deep link or the workbench). */
  source?: InstallSource;
  onClose: () => void;
  onInstalled: (install: InstallView) => void;
  /** Jump to the Installed tab. */
  onManage: () => void;
}) {
  const detail = useResource(() => apiGetListing(slug), slug);

  useEffect(() => sendListingEvent(slug, 'view', source), [slug, source]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const listing = detail.data;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-surface" role="dialog" aria-label="Listing details">
      <div className="flex items-center gap-2 border-b border-hairline px-4 py-3">
        <button type="button" onClick={onClose} className="ui-btn-icon" aria-label="Back to the marketplace">
          <div className="i-ph:arrow-left text-[14px]" />
        </button>
        <h3 className="min-w-0 truncate text-[13px] font-medium text-primary">{listing?.name ?? 'Marketplace'}</h3>
        <div className="flex-1" />
        {install && (
          <Pill tone={installStatusMeta(install.status).tone} label={installStatusMeta(install.status).label} />
        )}
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {listing ? (
          <ListingBody
            appId={appId}
            listing={listing}
            install={install}
            source={source}
            onInstalled={onInstalled}
            onManage={onManage}
          />
        ) : detail.error ? (
          <LoadError message="Could not load this listing." onRetry={() => void detail.reload()} />
        ) : (
          <>
            <SkeletonCard lines={3} />
            <SkeletonCard lines={4} />
          </>
        )}
      </div>
    </div>
  );
}

function ListingBody({
  appId,
  listing,
  install,
  source,
  onInstalled,
  onManage,
}: {
  appId: string;
  listing: ListingDetail;
  install: InstallView | null;
  source: InstallSource;
  onInstalled: (install: InstallView) => void;
  onManage: () => void;
}) {
  const isTheme = listing.kind === 'theme';
  const isPlugin = listing.kind === 'plugin';
  const isTemplate = listing.kind === 'template';
  const { capabilities } = listing;
  const dev = listing.developer;

  return (
    <>
      <header className="space-y-3">
        <div className="flex items-start gap-3">
          <ListingIcon url={listing.iconUrl} name={listing.name} size={44} />
          <div className="min-w-0 flex-1">
            <h2 className="font-instrument text-[24px] leading-[1.05] tracking-[-0.02em] text-primary">
              {listing.name}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <DeveloperLine developer={dev} />
              <span className="text-[11px] text-muted">v{listing.version}</span>
            </div>
          </div>
        </div>
        <p className="text-[12.5px] leading-relaxed text-secondary">{listing.tagline}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Stars rating={listing.rating} />
            {ratingSummary(listing.rating, listing.ratingCount)}
          </span>
          {!isTemplate && <span>{installsLabel(listing.installCount)}</span>}
          {isTemplate && listing.template && <span>{remixLabel(listing.template.remixCount)}</span>}
          <span className="text-secondary">
            {priceLabel(listing.pricing)}
            {listing.metered && ' + usage'}
          </span>
          <ListingBadges item={listing} />
        </div>
      </header>

      {isTemplate && listing.template && <TemplateSummary template={listing.template} />}
      {listing.kind === 'skill' && listing.skill && <SkillSummary skill={listing.skill} />}

      {isTheme && listing.theme && (
        <ThemePreview tokens={coerceThemeTokens(listing.theme.tokens)} name={listing.name} tall />
      )}

      {listing.screenshots.length > 0 && (
        <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1">
          {listing.screenshots
            .filter((src) => src.startsWith('https://'))
            .map((src) => (
              <a key={src} href={src} target="_blank" rel="noopener noreferrer" className="shrink-0 snap-start">
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-[140px] w-auto rounded-lg border border-hairline bg-sunken object-cover"
                />
              </a>
            ))}
        </div>
      )}

      {isPlugin && capabilities.agentTools.length > 0 && (
        <section className={classNames(CARD, 'space-y-3 border-accent/25 bg-accent/[0.04] p-4')}>
          <div className="flex items-center gap-2">
            <div className="i-ph:sparkle-fill text-[14px] text-accent" />
            <h4 className="text-[12.5px] font-medium text-primary">What the Dunea AI can do with it</h4>
          </div>
          <p className="text-[11.5px] leading-relaxed text-secondary">
            Once installed, the Dunea AI can use {listing.name} while it builds and runs your app. Just ask in the chat.
          </p>
          <ul className="space-y-2">
            {capabilities.agentTools.map((tool) => (
              <li key={tool.name} className="flex items-start gap-2">
                <div className="i-ph:arrow-elbow-down-right mt-0.5 shrink-0 text-[12px] text-accent" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="font-geist-mono text-[10.5px] text-accent/90">{tool.name}</code>
                    <span className="text-[10.5px] text-muted">
                      {tool.priceCredits ? `${creditsLabel(tool.priceCredits)} per call` : 'Free'}
                    </span>
                  </div>
                  <p className="text-[12px] leading-relaxed text-primary">{tool.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {isPlugin && listing.usage && (
        <Section title="Usage charges" icon="i-ph:gauge">
          <div className={classNames(CARD, 'space-y-1.5 p-4')}>
            <div className="text-[12.5px] text-primary">
              Up to {creditsLabel(listing.usage.maxCreditsPerUnit)} per {listing.usage.unit}
            </div>
            <p className="text-[11.5px] leading-relaxed text-secondary">{listing.usage.description}</p>
            <p className="text-[11px] text-muted">Charged to your credits, within the monthly cap you set.</p>
          </div>
        </Section>
      )}

      {isPlugin && (
        <Section title="What it can do" icon="i-ph:lightning">
          <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
            <CapabilityRow
              icon="i-ph:bell-ringing"
              title={capabilities.events.length ? 'Reacts to events' : 'No event subscriptions'}
              detail={capabilities.events.length ? capabilities.events.join(', ') : null}
              mono
            />
            <CapabilityRow
              icon="i-ph:sidebar-simple"
              title={capabilities.hasPanel ? 'Adds a panel to your workbench' : 'No workbench panel'}
            />
            <CapabilityRow
              icon="i-ph:code"
              title={
                capabilities.siteScripts
                  ? `${capabilities.siteScripts} ${capabilities.siteScripts === 1 ? 'script' : 'scripts'} on your published site`
                  : 'No scripts on your site'
              }
              detail={capabilities.siteScripts ? 'Reviewed by Dunea. Visitors see them only after consent.' : null}
            />
          </div>
        </Section>
      )}

      {isTheme && listing.theme && listing.theme.sections.length > 0 && (
        <Section title="Section recipes" icon="i-ph:layout">
          <div className="flex flex-wrap gap-1.5">
            {listing.theme.sections.map((s) => (
              <span key={s} className="ui-pill text-secondary">
                {s}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section title="About" icon="i-ph:text-align-left">
        <p className="whitespace-pre-line text-[12.5px] leading-relaxed text-secondary">{listing.description}</p>
      </Section>

      {isTemplate ? (
        <RemixBox listing={listing} source={source} />
      ) : (
        <InstallBox
          appId={appId}
          listing={listing}
          install={install}
          source={source}
          onInstalled={onInstalled}
          onManage={onManage}
        />
      )}

      <Section title="Developer" icon="i-ph:user-circle">
        <div className={classNames(CARD, 'space-y-2 p-4')}>
          <DeveloperLine developer={dev} />
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12px]">
            {dev.website && <ExternalLink href={dev.website} label="Website" />}
            <ExternalLink href={`mailto:${dev.supportEmail}`} label="Support" />
            <ExternalLink href={dev.privacyUrl} label="Privacy policy" />
          </div>
        </div>
      </Section>

      <ListingReviews listing={listing} install={install} />
    </>
  );
}

function TemplateSummary({ template }: { template: NonNullable<ListingDetail['template']> }) {
  const preview = safeHttpsUrl(template.previewUrl);

  return (
    <section className={classNames(CARD, 'space-y-3 p-4')}>
      {preview && (
        <a
          href={preview}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-primary underline decoration-subtle underline-offset-2 hover:decoration-prominent"
        >
          <div className="i-ph:browser text-[14px] text-muted" />
          Open the live preview
          <div className="i-ph:arrow-up-right text-[11px]" />
        </a>
      )}
      {template.pages.length > 0 && (
        <div className="space-y-1.5">
          <h4 className={CAPTION}>{template.pages.length === 1 ? '1 page' : `${template.pages.length} pages`}</h4>
          <div className="flex flex-wrap gap-1.5">
            {template.pages.map((p) => (
              <span key={p} className="ui-pill text-secondary">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
      {(template.theme || template.recommendedPlugins.length > 0) && (
        <div className="space-y-1 text-[11.5px] text-muted">
          {template.theme && (
            <div>
              Theme <code className="font-geist-mono text-[10.5px] text-secondary">{template.theme}</code>
            </div>
          )}
          {template.recommendedPlugins.length > 0 && (
            <div>
              Works best with{' '}
              {template.recommendedPlugins.map((p, i) => (
                <span key={p}>
                  {i > 0 && ', '}
                  <code className="font-geist-mono text-[10.5px] text-secondary">{p}</code>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SkillSummary({ skill }: { skill: NonNullable<ListingDetail['skill']> }) {
  return (
    <section className={classNames(CARD, 'space-y-3 p-4')}>
      <p className="text-[12px] leading-relaxed text-secondary">
        Once installed, the Dunea AI follows this skill while it builds this app.
      </p>
      {skill.triggers.length > 0 && (
        <div className="space-y-1.5">
          <h4 className={CAPTION}>Used for</h4>
          <div className="flex flex-wrap gap-1.5">
            {skill.triggers.map((t) => (
              <span key={t} className="ui-pill text-secondary">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
      {(skill.recommendedThemes.length > 0 || skill.recommendedPlugins.length > 0) && (
        <p className="text-[11.5px] text-muted">
          Pairs with{' '}
          {[...skill.recommendedThemes, ...skill.recommendedPlugins].map((p, i) => (
            <span key={p}>
              {i > 0 && ', '}
              <code className="font-geist-mono text-[10.5px] text-secondary">{p}</code>
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

/** Templates: "Start from this template" makes a new site (or leaves for Checkout when paid). */
function RemixBox({ listing, source }: { listing: ListingDetail; source: InstallSource }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paid = listing.pricing.model !== 'free';

  const start = async () => {
    sendListingEvent(listing.slug, 'install_click', source);
    setBusy(true);
    setError(null);

    try {
      const result = await remixTemplate(listing.slug);

      if (result === 'redirecting') {
        return;
      }

      toast.success(`Your copy of ${listing.name} is ready.`);
      navigate(result.path);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start from this template.'));
      setBusy(false);
    }
  };

  return (
    <section className={classNames(CARD, 'space-y-2 p-4')}>
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="ui-btn-accent w-full justify-center !py-2 !text-[12.5px] disabled:opacity-50"
      >
        {busy && <Spinner />}
        {paid ? `Start from this template · ${priceLabel(listing.pricing)}` : 'Start from this template'}
      </button>
      <p className="text-center text-[11px] text-muted">
        Makes a new site with its own chat. Only code and content are copied.
        {paid && " You'll pay on Stripe first."}
      </p>
      {error && <ErrorBanner message={error} />}
    </section>
  );
}

function ExternalLink({ href, label }: { href: string; label: string }) {
  // Developer-supplied: only https / mailto links are rendered.
  if (!/^(https:\/\/|mailto:)/i.test(href)) {
    return null;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
    >
      {label}
      <div className="i-ph:arrow-up-right text-[11px]" />
    </a>
  );
}

function CapabilityRow({
  icon,
  title,
  detail,
  mono,
}: {
  icon: string;
  title: string;
  detail?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-2.5 px-4 py-2.5">
      <div className={classNames(icon, 'mt-0.5 shrink-0 text-[13px] text-muted')} />
      <div className="min-w-0">
        <div className="text-[12px] text-primary">{title}</div>
        {detail && (
          <div className={classNames('break-words text-[11px] text-muted', mono && 'font-geist-mono text-[10.5px]')}>
            {detail}
          </div>
        )}
      </div>
    </div>
  );
}

/** Consent + settings + Install (the drawer's single lime action). */
function InstallBox({
  appId,
  listing,
  install,
  source,
  onInstalled,
  onManage,
}: {
  appId: string;
  listing: ListingDetail;
  install: InstallView | null;
  source: InstallSource;
  onInstalled: (install: InstallView) => void;
  onManage: () => void;
}) {
  const permissions = requestedPermissions(listing.permissions);
  const fields = listingSettingsFields(listing);
  const [consented, setConsented] = useState(false);
  const [values, setValues] = useState(() => initialSettingsValues(fields));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = settingsErrors(fields, values);
  const capNeeded = listing.metered || needsSpendCap(listing);
  const [capText, setCapText] = useState(() => String(defaultSpendCap(listing)));
  const cap = parseSpendCap(capText);
  const capErrors = capNeeded && cap.error ? { ...errors, spendCap: cap.error } : errors;
  const readiness = installReadiness({ permissionCount: permissions.length, consented, settingsErrors: capErrors });
  const isTheme = listing.kind === 'theme';
  const isSkill = listing.kind === 'skill';

  if (install && install.status !== 'uninstalled') {
    const meta = installStatusMeta(install.status);

    return (
      <section className={classNames(CARD, 'space-y-3 p-4')}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12.5px] text-primary">Installed on this app</span>
          <Pill tone={meta.tone} label={meta.label} />
        </div>
        {install.status === 'pending_payment' && (
          <p className="text-[11.5px] leading-relaxed text-warning">
            Checkout isn't finished yet. It activates as soon as the payment goes through.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {isTheme && install.status === 'active' && (
            <button
              type="button"
              onClick={() => requestApplyTheme(listing.name, listing.slug)}
              className="ui-btn-accent !px-4 !py-1.5 !text-[12px]"
            >
              <div className="i-ph:magic-wand text-[13px]" />
              Apply to this app
            </button>
          )}
          <button type="button" onClick={onManage} className={BTN_SECONDARY}>
            Manage
          </button>
        </div>
      </section>
    );
  }

  const submit = async () => {
    setTried(true);

    if (!readiness.ready) {
      return;
    }

    sendListingEvent(listing.slug, 'install_click', source);
    setBusy(true);
    setError(null);

    try {
      const result = await installListing(appId, {
        slug: listing.slug,
        source,
        permissions: permissions.map((p) => p.id),
        settings: fields.length ? settingsPayload(fields, values) : undefined,
        ...(capNeeded && cap.value !== null ? { spendCapCredits: cap.value } : {}),
      });

      if (result === 'redirecting') {
        return; // leaving for Stripe Checkout
      }

      toast.success(
        isSkill ? `${listing.name} installed. The AI uses it while it builds this app.` : `${listing.name} installed.`,
      );
      onInstalled(result);

      if (isTheme) {
        requestApplyTheme(listing.name, listing.slug);
      }
    } catch (err) {
      setError(apiErrorMessage(err, `Could not install ${listing.name}.`));
    } finally {
      setBusy(false);
    }
  };

  const price = priceLabel(listing.pricing);

  return (
    <section className={classNames(CARD, 'space-y-4 p-4')}>
      {permissions.length > 0 && (
        <div className="space-y-2.5">
          <h4 className={CAPTION}>{listing.name} will be able to</h4>
          <ul className="space-y-1.5">
            {permissions.map((p) => (
              <li key={p.id} className="flex items-start gap-2 text-[12px] leading-relaxed text-primary">
                <div className="i-ph:check-circle mt-0.5 shrink-0 text-[13px] text-muted" />
                <span>
                  {p.label} <code className="font-geist-mono text-[10px] text-muted">{p.id}</code>
                </span>
              </li>
            ))}
          </ul>
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-hairline bg-sunken px-3 py-2.5">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              className="mt-0.5 shrink-0 accent-accent"
            />
            <span className="text-[12px] leading-relaxed text-secondary">{consentSentence(listing.name)}</span>
          </label>
          {tried && !consented && <p className="text-[11px] text-danger">{readiness.reason}</p>}
        </div>
      )}

      {fields.length > 0 && (
        <div className="space-y-2.5">
          <h4 className={CAPTION}>Settings</h4>
          <SettingsForm
            fields={fields}
            values={values}
            errors={errors}
            onChange={setValues}
            disabled={busy}
            showErrors={tried}
          />
        </div>
      )}

      {capNeeded && (
        <div className="space-y-1.5">
          <h4 className={CAPTION}>Monthly spend cap</h4>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={capText}
              onChange={(e) => setCapText(e.target.value)}
              disabled={busy}
              aria-label="Monthly spend cap in credits"
              className="ui-input w-[120px] !py-2 [font-variant-numeric:tabular-nums]"
            />
            <span className="text-[12px] text-secondary">credits a month</span>
          </div>
          <p className="text-[11px] leading-relaxed text-muted">{SPEND_CAP_SENTENCE}</p>
          {tried && cap.error && <p className="text-[11px] text-danger">{cap.error}</p>}
        </div>
      )}

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || (tried && !readiness.ready)}
          className="ui-btn-accent w-full justify-center !py-2 !text-[12.5px] disabled:opacity-50"
        >
          {busy && <Spinner />}
          {listing.pricing.model === 'free' ? `Install ${listing.name}` : `Install · ${price}`}
        </button>
        {!tried && readiness.reason && !cap.error && (
          <p className="text-center text-[11px] text-muted">{readiness.reason}</p>
        )}
        {listing.pricing.model !== 'free' && (
          <p className="text-center text-[11px] text-muted">You'll finish payment on Stripe and come right back.</p>
        )}
        {error && <ErrorBanner message={error} />}
      </div>
    </section>
  );
}
